import moment from "moment";
import { CloudSearchType } from "./@types";
import { SEARCH_FIELD_ALIASES, SEARCH_FIELDS } from "./mappings";

const {
  CLASS,
  AVAILABLE: AVAILABLE_FIELD,
  SHAREABLE,
  IN_ALIAS,
  DOMAIN,
  IPV4,
  IPV4_STATIC,
  IPV4_DYNAMIC,
  IPV6,
  IPV6_STATIC,
  URI,
  URI_SOURCE,
  URI_STATIC,
  URI_DYNAMIC,
  URI_CONFIG,
  ITW,
  C2,
  IP,
  FS,
  FIRSTSEEN
} = SEARCH_FIELDS;

export const ISO_NO_MILLIS_MINUTES_HOURS_FORMAT_UTC = "YYYY-MM-DD";
export const ISO_YEAR_MONTH_DAY_FORMAT_UTC = "YYYY-MM-DD";
export const ISO_FORMAT_WITHOUT_MILLIS = "YYYY-MM-DD[T]HH:mm:ss[Z]";

export const commaSeparatedValuesRegex = /(,)(\s?)([a-zA-Z0-9])/g;
export const rangeSeparatedValuesRegex =
  /([a-zA-Z0-9]*)(\sTO\s)([a-zA-Z0-9]*)/gi;
export const operatorsRegex = /\s(and|or)(\snot)?\s/gi;
export const endingWithOperatorsRegex = /\s(AND|OR|NOT)(\s*)?$/;
export const isAvailableKeywordRegex =
  /(^|\s|\()(available|in|shareable):(\s*)true/i;
export const isNotAvailableKeywordRegex =
  /(^|\s|\()(available|in|shareable):(\s*)false/i;
export const timestampRegex = /\[|\]/g;
export const BULK_SEARCH_SPLIT_REGEX = /[,\n]/;
export const FIRST_COLON_ONLY_REGEX = /:(.*)/;
export const FIRST_SEEN_STRING = /firstseen:|fs:/;
export const MATCH_FIRST_SEEN = /(firstseen:|fs:)\s*(\[[^\]]+\])/;
export const MATCH_FIRST_SEEN_USER_FRIENDLY_SHORT =
  /(firstseen:|fs:)\s*(\d[hdwmy][+-])/i;
export const MATCH_FIRST_SEEN_USER_FRIENDLY_DATE =
  /(firstseen:|fs:)\s*(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z?[+-]?)/i;

export const SEARCH_QUERY_LIMIT = 1024;

export const gridDataFieldMap = {
  available: "sample_available",
  filecount: "file_count",
  classification: "threat_status",
  firstseen: "first_seen",
  lastseen: "last_seen",
  sampletype: "sample_type",
  size: "sample_size",
  threatname: "threat_name",
  filename: "file_name"
};

export const LOCATION = {
  LOCAL: "local",
  CLOUD: "cloud",
  NETWORK: "network",
  NOT_FOUND: "notfound_hashes",
  INVALID: "invalid_hashes"
};

export const ACTIVE_FILTER = {
  LOCAL: "local",
  CLOUD: "cloud",
  NETWORK: "network",
  NOT_FOUND: "notfound_hashes",
  INVALID: "invalid_hashes"
};

export const DATE_FILTER = {
  ALL: "all",
  CUSTOM: "custom",
  TODAY: "today",
  LAST_WEEK: "lastWeek",
  LAST_MONTH: "lastMonth",
  LAST_QUARTER: "lastQuarter",
  LAST_YEAR: "lastYear"
};

export const FILTER_KEYWORDS = {
  CLASSIFICATION: "classification",
  SUBMISSION_TIME: "submission-time",
  SUBMISSION_USER: "submission-user",
  PROCESSING_STATUS: "processing-status",
  SAMPLE_ORIGIN: "sample_origin",
  TIME_TYPE: "time_type",
  PERIOD_TYPE: "period_type",
  LAST_SEEN: "lastseen",
  LAST_SEEN_ALIAS: "ls",
  FIRST_SEEN: "firstseen",
  FIRST_SEEN_ALIAS: "fs",
  CLOUD_SEARCH_TYPE: "cloud_search_type"
};

export const CLASSIFICATION_KEYWORDS = {
  MALICIOUS: "malicious",
  SUSPICIOUS: "suspicious",
  GOODWARE: "goodware",
  UNKNOWN: "unknown"
};

export const COLUMNS_KEYWORDS = {
  SUBMISSION_TIME: "submission_time",
  SUBMISSION_USER: "submission_user",
  LAST_SEEN: "last_seen",
  YARA_RULE: "yara_rule"
};

export const AVAILABLE_TRUE_QP = "available:true";
export const AVAILABLE_FALSE_QP = "available:false";

export const AVAILABLE: {
  [key: string]: boolean;
} = {
  shareable: true,
  private: false
};

export const pageSizeSelections = [
  { label: "20", value: "20" },
  { label: "100", value: "100" }
];

export const CLOUD_SEARCH_TYPE: Record<string, CloudSearchType> = {
  ALL: "all",
  PRIVATE: "private",
  PUBLIC: "public"
};

// Date of the oldest sample on Spectra Intelligence
// Currently, there is a bug on Spectra Intelligence where they return 500 when date is out of limit
export const CLOUD_LIMIT_DATE = "2010-05-01";
export const CLOUD_LIMIT_MOMENT = moment(
  CLOUD_LIMIT_DATE,
  ISO_YEAR_MONTH_DAY_FORMAT_UTC
);

export const SEARCH_FIELD_TO_FILTER_MAP = {
  [FILTER_KEYWORDS.CLASSIFICATION]: FILTER_KEYWORDS.CLASSIFICATION,
  [CLASS]: FILTER_KEYWORDS.CLASSIFICATION,
  [AVAILABLE_FIELD]: FILTER_KEYWORDS.CLOUD_SEARCH_TYPE,
  [IN_ALIAS]: FILTER_KEYWORDS.CLOUD_SEARCH_TYPE,
  [SHAREABLE]: FILTER_KEYWORDS.CLOUD_SEARCH_TYPE,
  [FILTER_KEYWORDS.SUBMISSION_USER]: FILTER_KEYWORDS.SUBMISSION_USER,
  [FILTER_KEYWORDS.PROCESSING_STATUS]: FILTER_KEYWORDS.PROCESSING_STATUS
};

export const DATE_FIELDS = [
  SEARCH_FIELDS.FIRSTSEEN,
  SEARCH_FIELD_ALIASES.FS,
  SEARCH_FIELDS.LASTSEEN,
  SEARCH_FIELD_ALIASES.LS,
  SEARCH_FIELDS.PE_TIMESTAMP,
  SEARCH_FIELDS.PETS,
  SEARCH_FIELDS.SUBMISSION_TIME,
  SEARCH_FIELDS.LAST_ANALYSIS,
  SEARCH_FIELD_ALIASES.LA,
  SEARCH_FIELDS.SIGNER_VALID_FROM,
  SEARCH_FIELD_ALIASES.CERT_VALID_FROM,
  SEARCH_FIELDS.SIGNER_VALID_TO,
  SEARCH_FIELD_ALIASES.CERT_VALID_TO,
  SEARCH_FIELDS.TAGGANT_VALID_FROM,
  SEARCH_FIELDS.TAGGANT_VALID_TO
];

export const NTI_KEYWORDS = [
  DOMAIN,
  IP,
  IPV4,
  IPV4_STATIC,
  IPV4_DYNAMIC,
  IPV6,
  IPV6_STATIC,
  URI,
  URI_SOURCE,
  URI_STATIC,
  URI_DYNAMIC,
  URI_CONFIG,
  ITW,
  C2
];

export const NTI_KEYWORD_VALUE_REGEX = new RegExp(
  `(${NTI_KEYWORDS.join("|")})\\S+`
);

export const NETWORK_TAB_KEYWORDS = [...NTI_KEYWORDS, FS, FIRSTSEEN];

const NON_NETWORK_TAB_KEYWORDS = Object.values(SEARCH_FIELDS).filter(
  value => !NETWORK_TAB_KEYWORDS.includes(value)
);
export const NON_NETWORK_TAB_KEYWORD_VALUE_REGEX = new RegExp(
  `(\\s|^)(${NON_NETWORK_TAB_KEYWORDS.join("|")})\\S+`,
  "g"
);

export const MAX_NETWORK_VALUE_LENGTH = 2000;
export const NETWORK_SEARCH_LIMIT_ERROR_MESSAGE =
  "You can only search for up to 1000 URLs, Domains or IP Addresses.";
export const NETWORK_SEARCH_LIMIT = 1000;

export const GRID_SELECTION_TAG_LIMIT = 100;
